"use client"

import { useEffect, useState } from "react"
import { Printer, ChevronDown } from "lucide-react"

interface Section {
  id: string
  title: string
}

/**
 * Sticky sidebar with table of contents for the legal pages.
 *
 * Reads the sections from the main <article data-legal-sections="..." /> element
 * so we don't have to pass them via props through a server → client boundary.
 * Highlights the section currently in the viewport using IntersectionObserver.
 */
export function LegalTOC() {
  const [sections, setSections] = useState<Section[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  // Pull section list from the rendered article (set via data-legal-sections).
  useEffect(() => {
    const article = document.querySelector<HTMLElement>("article[data-legal-sections]")
    if (!article) return
    try {
      const parsed = JSON.parse(article.dataset.legalSections ?? "[]") as Section[]
      setSections(parsed)
    } catch {/* noop */}
  }, [])

  // Active-section tracking.
  useEffect(() => {
    if (sections.length === 0) return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible[0]) setActiveId(visible[0].target.id)
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: [0, 0.25, 0.5, 1] }
    )
    for (const s of sections) {
      const el = document.getElementById(s.id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [sections])

  const go = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (!el) return
    const top = el.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top, behavior: "smooth" })
    window.history.replaceState(null, "", `#${id}`)
    setMobileOpen(false)
  }

  const handlePrint = () => {
    if (typeof window !== "undefined") window.print()
  }

  if (sections.length === 0) return null

  const activeSection = sections.find((s) => s.id === activeId) ?? sections[0]

  return (
    <>
      {/* ── Desktop: sticky sidebar ───────────────────────────────── */}
      <aside className="hidden lg:block">
        <div className="sticky top-24 w-60 flex-shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[rgba(10,10,10,0.30)] mb-3 px-2">
            Contents
          </p>
          <nav className="space-y-0.5">
            {sections.map((s) => {
              const active = s.id === activeId
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={go(s.id)}
                  className={`
                    block px-2 py-1.5 rounded-lg text-[12px] leading-snug border-l-2 transition-colors duration-150
                    ${active
                      ? "border-[#0F5132] text-[#0A0A0A] bg-[rgba(10,10,10,0.04)]"
                      : "border-transparent text-[rgba(10,10,10,0.45)] hover:text-[rgba(10,10,10,0.70)] hover:bg-[rgba(10,10,10,0.02)]"
                    }
                  `}
                >
                  {s.title}
                </a>
              )
            })}
          </nav>

          <button
            onClick={handlePrint}
            className="mt-5 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-[12px] font-medium border border-[rgba(10,10,10,0.10)] bg-[rgba(10,10,10,0.02)] text-[rgba(10,10,10,0.55)] hover:text-[#0F5132] hover:bg-[rgba(10,10,10,0.06)] hover:border-white/[0.14] transition-all duration-150"
          >
            <Printer size={11} />
            Print / Save as PDF
          </button>
        </div>
      </aside>

      {/* ── Mobile: collapsible dropdown pinned near the top ───────── */}
      <div className="lg:hidden mb-6">
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border border-[rgba(10,10,10,0.10)] bg-[rgba(10,10,10,0.02)] text-left"
          aria-expanded={mobileOpen}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-[rgba(10,10,10,0.30)]">
              Contents
            </span>
            <span className="text-[12px] text-[rgba(10,10,10,0.65)] truncate">
              · {activeSection.title}
            </span>
          </div>
          <ChevronDown
            size={14}
            className={`text-[rgba(10,10,10,0.42)] flex-shrink-0 transition-transform duration-200 ${mobileOpen ? "rotate-180" : ""}`}
          />
        </button>

        {mobileOpen && (
          <div className="mt-2 p-2 rounded-xl border border-[rgba(10,10,10,0.10)] bg-[#FFFFFF] space-y-0.5">
            {sections.map((s) => {
              const active = s.id === activeId
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={go(s.id)}
                  className={`
                    block px-3 py-2 rounded-lg text-[12.5px] leading-snug transition-colors duration-150
                    ${active
                      ? "text-[#0A0A0A] bg-[rgba(10,10,10,0.06)]"
                      : "text-[rgba(10,10,10,0.65)] hover:text-[#0F5132] hover:bg-[rgba(10,10,10,0.04)]"
                    }
                  `}
                >
                  {s.title}
                </a>
              )
            })}
            <button
              onClick={handlePrint}
              className="mt-1 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-[12px] font-medium border border-[rgba(10,10,10,0.10)] bg-[rgba(10,10,10,0.02)] text-[rgba(10,10,10,0.65)] hover:text-[#0F5132] hover:bg-[rgba(10,10,10,0.06)] transition-all duration-150"
            >
              <Printer size={11} />
              Print / Save as PDF
            </button>
          </div>
        )}
      </div>
    </>
  )
}
